import { FaqSection, FaqSectionId } from '@app/types/faq.types';

export const FAQ_SECTIONS: FaqSection[] = [
  {
    id: 'future-order',
    routePrefix: '/future-order',
    title: 'Future Order Tool',
    shortTitle: 'Future Order',
    icon: 'calendar-outline',
    description: 'Schedule advance customer orders outside standard ordering cycles to safeguard supply during peak periods or shutdowns.',
    items: [
      {
        id: 'fo-1',
        question: 'What is the purpose of the Future Order Tool?',
        answer: 'The Future Order Tool allows Sales and Customer Service representatives to schedule orders in advance for specific customers beyond the standard operational ordering window. This ensures demand visibility and prevents fulfillment stockouts during holidays, planned warehouse shutdowns, or customer closure periods.',
        tags: ['future order', 'advance orders', 'scheduling', 'shutdown', 'holiday']
      },
      {
        id: 'fo-2',
        question: 'What is the difference between "From Order Date" and "To Order Date"?',
        answer: '"From Order Date" defines the starting day OrderSmart starts generating advance orders, whereas "To Order Date" specifies the final order generation date. Orders are submitted according to the customer\'s predefined delivery profile and route schedule.',
        tags: ['dates', 'from date', 'to date', 'order date']
      },
      {
        id: 'fo-3',
        question: 'Who has access to "Manage Hierarchy" and how does bottler mapping work?',
        answer: '"Manage Hierarchy" is restricted to Future Order Tool Administrators (<code>isFotAdmin</code>) and District Sales Managers (<code>isDsm</code>) belonging to authorized bottlers (e.g., Bottler IDs 5000, 4200, 4600). It manages customer grouping, supervisor approval chains, and territory visibility.',
        tags: ['hierarchy', 'admin', 'dsm', 'bottler', 'permissions']
      },
      {
        id: 'fo-4',
        question: 'Can I submit same-day future orders?',
        answer: 'No. Orders must be scheduled at least 1 day in advance (UTC start-of-day cut-off). The tool automatically enforces the earliest selectable date as tomorrow\'s date.',
        tags: ['same-day', 'lead time', 'cutoff', 'validation']
      },
      {
        id: 'fo-5',
        question: 'How do I edit or cancel an existing Future Order request?',
        answer: 'Navigate to the "Request Orders" tab. Locate your request in the table, click the Actions menu (three dots or edit/delete icon), and choose "Edit" to modify dates and quantities or "Delete" to cancel pending order generation.',
        tags: ['edit', 'cancel', 'delete', 'actions']
      }
    ]
  },
  {
    id: 'forecast-lab',
    routePrefix: '/',
    title: 'Forecast Lab (Bump Up)',
    shortTitle: 'Forecast Lab',
    icon: 'trending-up',
    description: 'Central workspace to inspect baseline forecasts and create Ad Hoc or Bulk Bump-Up requests to accommodate expected spikes.',
    items: [
      {
        id: 'fl-1',
        question: 'What does Forecast Lab do?',
        answer: 'Forecast Lab is the central intelligence workspace where planners analyze baseline demand forecasts, review OrderSmart suggested order quantities, and place <b>Ad Hoc</b> or <b>Bulk Bump-Up</b> requests to elevate ordering volume for promotional periods or local events.',
        tags: ['forecast', 'bump up', 'baseline', 'ordersmart']
      },
      {
        id: 'fl-2',
        question: 'What is a "Bump-Up Request" and how does it affect orders?',
        answer: 'A Bump-Up Request injects an incremental adjustment (percentage increase or fixed quantity) on selected customer-material pairs over a designated date range. OrderSmart recalculates the suggested orders by blending the statistical baseline with your bump-up parameters.',
        tags: ['bump up', 'percentage', 'quantity', 'suggested order']
      },
      {
        id: 'fl-3',
        question: 'What are the daily cut-off times for active bump-up changes?',
        answer: 'The standard system synchronization cut-off occurs at <b>11:30 PM UTC (23:30 UTC)</b>. Changes confirmed before 11:30 PM UTC apply to orders generated the next calendar day. Modifications submitted after 11:30 PM UTC take effect the day after next.',
        tags: ['cut-off', 'timing', 'utc', 'synchronization']
      },
      {
        id: 'fl-4',
        question: 'How do I view the performance and fill rate of my bump-up?',
        answer: 'Click the "Insights" icon button in the Actions column of any bump-up request in the table. The Bump-Up Insights modal displays Order Metrics (actual vs. suggested), Volume Metrics, Fill Rate percentages, and provides an exportable detailed breakdown.',
        tags: ['insights', 'fill rate', 'metrics', 'performance']
      },
      {
        id: 'fl-5',
        question: 'What is the difference between Ad Hoc and Bulk Request tabs?',
        answer: 'The <b>Ad Hoc Request</b> tab is designed for quick, single-entity or targeted customer adjustments using interactive form filters. The <b>Bulk Request</b> tab allows you to download an Excel/CSV template, populate dozens of customer-material pairs, and upload them simultaneously.',
        tags: ['ad hoc', 'bulk request', 'template', 'upload']
      },
      {
        id: 'fl-6',
        question: 'How many orders does OrderSmart need before it starts predicting?',
        answer: 'A product needs to have at least <b>3 invoices in a year</b> for OrderSmart to suggest an order for it.',
        tags: ['minimum orders', 'prediction', 'invoices', 'getting started']
      },
      {
        id: 'fl-7',
        question: 'A store across the street closed — how fast does OrderSmart adjust?',
        answer: 'Typically <b>3–5 data points (orders)</b> are needed for the learning module to detect and adjust for the change in sales volume.',
        tags: ['adjustment', 'store closure', 'learning', 'data points']
      },
      {
        id: 'fl-8',
        question: 'I ordered 2 extra cases manually — will OrderSmart repeat that every time?',
        answer: 'No. The learning module learns from the <b>full historical order pattern</b>, not just the last order. If an extra 2 cases was a one-off, OrderSmart will not carry that forward.',
        tags: ['manual order', 'learning', 'one-off', 'pattern']
      },
      {
        id: 'fl-9',
        question: 'How do I stop OrderSmart from predicting a specific product (e.g. after a store reset)?',
        answer: 'Two options:<br>1. <b>Remove the material from AML</b> — OrderSmart will not predict any material absent from the Approved Materials List.<br>2. <b>Persistence Logic</b> — after a certain number of consecutive rejections, OrderSmart automatically stops suggesting until a new order is placed.',
        tags: ['stop predicting', 'store reset', 'AML', 'persistence logic', 'rejections']
      },
      {
        id: 'fl-10',
        question: 'When does OrderSmart automatically stop predicting a product?',
        answer: 'OrderSmart stops predicting a product when:<ul><li>Fewer than <b>3 invoices</b> in a rolling year.</li><li>More than <b>56 days</b> gap between orders (91 days for low-frequency items).</li><li><b>Persistence Logic</b> triggers: repeated rejections exceed the threshold for the mover tier.</li></ul>',
        tags: ['stop predicting', 'gap', 'invoice threshold', 'persistence logic', 'frequency']
      },
      {
        id: 'fl-11',
        question: 'How does SKU layering and pallet rounding affect OrderSmart predictions?',
        answer: 'OrderSmart checks order quantities placed in the <b>past 3 months</b> and predicts the quantity that was ordered more than <b>10% of the time</b> — effectively reflecting your most common order size.',
        tags: ['sku layering', 'pallet rounding', 'order quantity', 'history']
      },
      {
        id: 'fl-12',
        question: 'Does OrderSmart react to promotional pricing?',
        answer: 'Currently, <b>no</b>. OrderSmart has very limited coverage of promotional data, so it does not adjust predictions based on promotional pricing at this time.',
        tags: ['promotions', 'pricing', 'promo', 'coverage']
      },
      {
        id: 'fl-13',
        question: 'Does OrderSmart use scan data from my store?',
        answer: 'Currently OrderSmart uses scan data for: <b>Walmart Canada, Target, CVS, Walgreens,</b> and <b>Dollar General</b>.',
        tags: ['scan data', 'retail', 'walmart', 'target', 'cvs', 'walgreens']
      },
      {
        id: 'fl-14',
        question: 'How does OrderSmart account for holidays?',
        answer: 'Holiday effects are incorporated directly into the forecasting models, using the <b>holiday lift or suppression patterns observed in previous years</b> for each customer-product pair.',
        tags: ['holidays', 'seasonality', 'forecasting', 'historical']
      },
      {
        id: 'fl-15',
        question: 'Does OrderSmart increase suggested orders in summer?',
        answer: 'Yes. <b>Seasonality</b> is a core module built into OrderSmart\'s forecasting models, so summer uplifts (and other seasonal trends) are automatically factored into predictions.',
        tags: ['summer', 'seasonality', 'seasonal', 'forecasting']
      },
      {
        id: 'fl-16',
        question: 'What is the difference between High, Medium, and Low movers?',
        answer: 'OrderSmart classifies products by annual invoice frequency:<ul><li><b>High movers:</b> 26+ invoices per year</li><li><b>Medium movers:</b> 20–26 invoices per year</li><li><b>Low movers:</b> Below 20 invoices per year</li></ul>This classification also drives Persistence Logic thresholds.',
        tags: ['high mover', 'medium mover', 'low mover', 'frequency', 'classification']
      },
      {
        id: 'fl-17',
        question: 'A customer refused or removed a product — will OrderSmart keep suggesting it?',
        answer: 'OrderSmart applies <b>Persistence Logic</b> based on mover tier before stopping:<ul><li><b>High movers:</b> 4 rejections within 21 days</li><li><b>Medium movers:</b> 5 rejections within 28 days</li><li><b>Slow movers:</b> 6 rejections within 35 days</li></ul>After the threshold is hit, OrderSmart stops suggesting until a new order is placed.',
        tags: ['rejection', 'persistence logic', 'refused', 'removed', 'high mover', 'slow mover']
      },
      {
        id: 'fl-18',
        question: 'What happens if a product is Out of Stock (OOS) in the warehouse?',
        answer: 'If an order quantity is greater than 0 but the picked quantity is null, OrderSmart flags it as a <b>warehouse OOS event</b>. On the next order date, the same actual order quantity is repeated so the customer is not under-served once stock is restored.',
        tags: ['out of stock', 'OOS', 'warehouse', 'picked quantity', 'flag']
      },
      {
        id: 'fl-19',
        question: 'What is the difference between the OrderSmart (OS) value and Average Order Quantity?',
        answer: '<b>OS</b> is the predicted (suggested) order quantity generated by the forecasting engine. <b>Average Actual Quantity</b> is a dashboard metric calculated from historical completed orders and is used for reference and comparison — it does not directly drive OrderSmart predictions.',
        tags: ['OS', 'average order quantity', 'predicted', 'actual', 'comparison', 'dashboard']
      }
    ]
  },
  {
    id: 'material-mapping',
    routePrefix: '/material-mapping',
    title: 'Material Mapping',
    shortTitle: 'Material Mapping',
    icon: 'swap-horizontal-outline',
    description: 'Seamlessly substitute obsolete or transitioning SKUs with new material numbers without losing sales history.',
    items: [
      {
        id: 'mm-1',
        question: 'When should I create a Material Mapping request?',
        answer: 'Material Mapping is used during SKU transitions, package redesigns, reformulations, or product phase-outs where an old Material SKU needs to be systematically substituted by a new Material SKU without interrupting customer ordering history.',
        tags: ['material mapping', 'sku transition', 'substitution', 'packaging']
      },
      {
        id: 'mm-2',
        question: 'Does Material Mapping transfer historical demand to the new material?',
        answer: 'Yes. OrderSmart transfers the historical sales and run-rate baseline of the obsolete SKU to the replacement SKU so replenishment algorithms produce accurate forecasts without waiting for weeks of new sales history.',
        tags: ['historical demand', 'history', 'baseline', 'algorithm']
      },
      {
        id: 'mm-3',
        question: 'What happens when I delete an active Material Mapping request?',
        answer: 'If deleted before the scheduled end date, the system reverts back to predicting the original material starting from the next cutoff synchronization date (subject to the 11:30 PM UTC window).',
        tags: ['delete', 'revert', 'expiration', 'end date']
      },
      {
        id: 'mm-4',
        question: 'What is the format for Bulk Upload in Material Mapping?',
        answer: 'Upload an Excel (<code>.xlsx</code>) or CSV file containing <code>Old Material</code>, <code>New Material</code>, <code>Start Date</code>, and <code>End Date</code>. The tool validates material existence, active statuses, and date bounds prior to saving.',
        tags: ['bulk upload', 'excel', 'csv', 'columns', 'validation']
      },
      {
        id: 'mm-5',
        question: 'A product changed version or package — what happens to OrderSmart predictions?',
        answer: 'OrderSmart predicts using <b>5 product attributes</b>: product, package, state, type, and size. If any of these change, the product is treated as entirely new and OrderSmart starts from scratch — requiring a minimum of <b>3 orders</b> before predictions resume.',
        tags: ['version change', 'package change', 'new product', 'attributes', 'restart']
      }
    ]
  },
  {
    id: 'innovation-prediction',
    routePrefix: '/innovation-prediction',
    title: 'Innovation Prediction',
    shortTitle: 'Innovation',
    icon: 'bulb-outline',
    description: 'Configure initial demand estimates, launch quantities, and delivery schedules for newly introduced products.',
    items: [
      {
        id: 'ip-1',
        question: 'What is Innovation Prediction?',
        answer: 'Innovation Prediction configures launch demand for newly introduced brand innovations and experimental SKUs that have zero historical transaction records.',
        tags: ['innovation', 'new sku', 'launch', 'new product']
      },
      {
        id: 'ip-2',
        question: 'How does OrderSmart forecast items with no prior sales history?',
        answer: 'The system utilizes customer grouping, planned delivery frequencies (e.g., Weekly, Bi-weekly, or Custom schedule), target launch quantities, and look-alike reference models to estimate initial inventory push and sustained replenishment.',
        tags: ['forecasting', 'no history', 'delivery frequency', 'replenishment']
      },
      {
        id: 'ip-3',
        question: 'What is the AML (Automated Machine Learning) Status?',
        answer: 'The AML status indicates whether the predictive engine has successfully incorporated the innovation item into the production training pipeline and generated valid confidence intervals.',
        tags: ['aml', 'machine learning', 'status', 'confidence interval']
      },
      {
        id: 'ip-5',
        question: 'Will OrderSmart predict a product that has been removed from the AML?',
        answer: 'No. OrderSmart will <b>not</b> predict or suggest orders for any material that has been removed from the Approved Materials List (AML).',
        tags: ['AML', 'approved materials list', 'removed', 'no prediction']
      },
      {
        id: 'ip-4',
        question: 'Can Innovation requests also be uploaded in bulk?',
        answer: 'Yes! The Bulk Request tab provides a dedicated Innovation template specifying <code>Material Number</code>, <code>Innovation Material Number</code>, <code>Start Date</code>, <code>End Date</code>, <code>Quantity</code>, and <code>Frequency</code>.',
        tags: ['bulk', 'template', 'frequency', 'quantity']
      }
    ]
  }
];

export const ROUTE_TO_SECTION_MAP: Record<string, FaqSectionId> = {
  'future-order': 'future-order',
  'material-mapping': 'material-mapping',
  'innovation-prediction': 'innovation-prediction',
  '': 'forecast-lab',
};

export function resolveFaqSectionFromUrl(url: string): FaqSectionId {
  if (!url) return 'forecast-lab';
  const cleanPath = url.split('?')[0].split('#')[0];
  const firstSegment = cleanPath.split('/')[1] || '';
  return ROUTE_TO_SECTION_MAP[firstSegment] || 'forecast-lab';
}
